<template>
  <div class="tenant-access-page">
    <div class="page-header">
      <div class="page-header__copy">
        <p class="eyebrow">TENANT CONTROL</p>
        <h1>自社権限管理</h1>
        <p>自社スタッフに割り当てる役割と、各役割で利用できる機能を管理します。</p>
      </div>
      <CButton class="page-header__action" color="primary" @click="openCreateRole">
        役割を追加
      </CButton>
    </div>

    <CAlert v-if="successMessage" color="success" close-button @update:show="successMessage = ''">
      {{ successMessage }}
    </CAlert>
    <CAlert v-if="errorMessage" color="danger" close-button @update:show="errorMessage = ''">
      {{ errorMessage }}
    </CAlert>
    <TenantOwnerManagement />

    <CAlert v-if="!loading" color="info" class="plan-scope-alert">
      現在の契約プランは<strong>{{ currentPlanName }}</strong>です。
      役割へ新しく設定できるのは、契約プランまたは企業別設定で利用可能な機能だけです。
      契約対象外の機能はグレー表示され、選択できません。
    </CAlert>

    <CCard class="access-card">
      <CCardHeader><strong>スタッフごとの役割</strong></CCardHeader>
      <CCardBody>
        <div v-if="loading" class="text-center py-5"><CSpinner color="primary"/></div>
        <template v-else>
          <div class="desktop-only responsive-table-shell">
            <CDataTable
              :items="normalizedUsers"
              :fields="userFields"
              bordered
              striped
              hover
              pagination
              :items-per-page="10"
            >
              <template #roles="{item}">
                <td>
                  <div class="role-badge-list">
                    <CBadge v-for="code in item.roleCodes" :key="code" color="info">
                      {{ roleLabel(code) }}
                    </CBadge>
                    <span v-if="!item.roleCodes.length" class="text-muted">役割未設定</span>
                  </div>
                </td>
              </template>
              <template #actions="{item}">
                <td>
                  <div class="desktop-role-controls">
                    <CSelect
                      :options="roleOptions"
                      :value="selectedRoleByUser[item.userId]"
                      @update:value="value => setSelectedRole(item.userId, value)"
                    />
                    <div class="desktop-role-buttons">
                      <CButton size="sm" color="primary" :disabled="saving" @click="assignRole(item)">追加</CButton>
                      <CButton size="sm" color="danger" variant="outline" :disabled="saving" @click="removeRole(item)">解除</CButton>
                    </div>
                  </div>
                </td>
              </template>
            </CDataTable>
          </div>

          <div class="mobile-only">
            <div class="mobile-record-list" aria-label="スタッフごとの役割一覧">
              <article v-for="user in mobileUsers" :key="user.userId" class="mobile-record-card">
                <div class="mobile-record-card__header">
                  <strong>{{ user.name || '氏名未設定' }}</strong>
                  <span>{{ user.email || 'メールアドレス未設定' }}</span>
                </div>

                <section class="mobile-section">
                  <h3>現在の役割</h3>
                  <div class="role-badge-list">
                    <CBadge v-for="code in user.roleCodes" :key="code" color="info">
                      {{ roleLabel(code) }}
                    </CBadge>
                    <span v-if="!user.roleCodes.length" class="text-muted">役割未設定</span>
                  </div>
                </section>

                <section class="mobile-section mobile-section--controls">
                  <label>変更する役割</label>
                  <CSelect
                    :options="roleOptions"
                    :value="selectedRoleByUser[user.userId]"
                    @update:value="value => setSelectedRole(user.userId, value)"
                  />
                  <div class="mobile-button-grid">
                    <CButton color="primary" :disabled="saving" @click="assignRole(user)">追加</CButton>
                    <CButton color="danger" variant="outline" :disabled="saving" @click="removeRole(user)">解除</CButton>
                  </div>
                </section>
              </article>

              <div v-if="!normalizedUsers.length" class="mobile-empty-state">
                対象スタッフがいません。
              </div>
            </div>

            <nav v-if="mobileUserPageCount > 1" class="mobile-pager" aria-label="スタッフ一覧ページ">
              <CButton
                color="secondary"
                variant="outline"
                :disabled="mobileUserPage <= 1"
                @click="setMobileUserPage(mobileUserPage - 1)"
              >
                前へ
              </CButton>
              <span>{{ mobileUserPage }} / {{ mobileUserPageCount }}</span>
              <CButton
                color="secondary"
                variant="outline"
                :disabled="mobileUserPage >= mobileUserPageCount"
                @click="setMobileUserPage(mobileUserPage + 1)"
              >
                次へ
              </CButton>
            </nav>
          </div>
        </template>
      </CCardBody>
    </CCard>

    <CCard class="access-card">
      <CCardHeader>
        <strong>自社の役割</strong>
        <div class="text-muted small mt-1">
          一般社員・事務担当者などの標準役割も、自社だけの利用機能へ変更できます。他社の設定には影響しません。
        </div>
      </CCardHeader>
      <CCardBody>
        <div class="desktop-only responsive-table-shell">
          <CDataTable :items="normalizedRoles" :fields="roleFields" bordered striped hover>
            <template #settingType="{item}">
              <td><CBadge :color="settingBadgeColor(item)">{{ settingLabel(item) }}</CBadge></td>
            </template>
            <template #capabilities="{item}">
              <td>
                <div v-if="item.activeCapabilityGroups.length" class="capability-list">
                  <div v-for="group in item.activeCapabilityGroups" :key="group.name" class="capability-row">
                    <strong>{{ group.name }}</strong>
                    <span>{{ group.items.join('、') }}</span>
                  </div>
                </div>
                <span v-else class="text-muted">現在の契約で利用できる機能が設定されていません</span>

                <div v-if="item.dormantCapabilityGroups.length" class="dormant-capability-block">
                  <strong>契約対象外として保持</strong>
                  <div v-for="group in item.dormantCapabilityGroups" :key="'dormant-' + group.name" class="dormant-capability-row">
                    <span>{{ group.name }}</span>
                    <small>{{ group.items.join('、') }}</small>
                  </div>
                </div>
              </td>
            </template>
            <template #actions="{item}">
              <td>
                <CButton
                  v-if="item.code !== 'TENANT_OWNER'"
                  size="sm"
                  color="info"
                  variant="outline"
                  @click="openPermissionEdit(item)"
                >
                  {{ item.standardRole && !item.customized ? '自社用に変更' : '利用機能を編集' }}
                </CButton>
                <span v-else class="text-muted">最高管理者は変更不可</span>
              </td>
            </template>
          </CDataTable>
        </div>

        <div class="mobile-only mobile-record-list" aria-label="自社の役割一覧">
          <article v-for="role in normalizedRoles" :key="role.id" class="mobile-record-card">
            <div class="mobile-role-header">
              <strong>{{ role.name }}</strong>
              <CBadge :color="settingBadgeColor(role)">{{ settingLabel(role) }}</CBadge>
            </div>

            <section class="mobile-section">
              <h3>現在の契約でできること</h3>
              <div v-if="role.activeCapabilityGroups.length" class="capability-list">
                <div v-for="group in role.activeCapabilityGroups" :key="group.name" class="capability-row">
                  <strong>{{ group.name }}</strong>
                  <span>{{ group.items.join('、') }}</span>
                </div>
              </div>
              <span v-else class="text-muted">現在の契約で利用できる機能が設定されていません</span>
            </section>

            <section v-if="role.dormantCapabilityGroups.length" class="mobile-section dormant-mobile-section">
              <h3>契約対象外として保持</h3>
              <div class="capability-list">
                <div v-for="group in role.dormantCapabilityGroups" :key="'mobile-dormant-' + group.name" class="capability-row capability-row--dormant">
                  <strong>{{ group.name }}</strong>
                  <span>{{ group.items.join('、') }}</span>
                </div>
              </div>
            </section>

            <CButton
              v-if="role.code !== 'TENANT_OWNER'"
              class="mobile-primary-action"
              color="info"
              variant="outline"
              @click="openPermissionEdit(role)"
            >
              {{ role.standardRole && !role.customized ? '自社用に変更' : '利用機能を編集' }}
            </CButton>
            <div v-else class="protected-role-note">
              最高管理者は安全のため変更できません。
            </div>
          </article>

          <div v-if="!normalizedRoles.length" class="mobile-empty-state">
            利用できる役割がありません。
          </div>
        </div>
      </CCardBody>
    </CCard>

    <CModal
      title="自社の役割を追加"
      :show.sync="roleModal"
      size="lg"
      :close-on-backdrop="false"
      class="access-modal"
    >
      <!--
        **CInput の label 属性では必須の印を入れられない。**そのため label を
        外して、印つきの label を自分で書く。

        役割名は必須のままにする。役割一覧でもスタッフへの付与画面でも、
        **名前がその役割を指す唯一の手がかり**だから。印が無いと、空のまま
        作成を押して初めて必須だったと気づくことになる。
      -->
      <label>役割名<span class="asterisk">*</span></label>
      <CInput v-model.trim="roleForm.roleName" placeholder="例：営業管理者"/>
      <CTextarea
        label="説明"
        v-model.trim="roleForm.description"
        placeholder="この役割を付与する対象や担当業務を入力してください。"
      />
      <CAlert color="info" class="mt-3">
        現在の契約プランは<strong>{{ currentPlanName }}</strong>です。
        グレー表示の機能はこの契約では選択できません。
      </CAlert>
      <h6 class="mt-4">利用できる機能</h6>
      <div v-for="group in groupedPermissions" :key="group.name" class="permission-group">
        <h6>{{ group.name }}</h6>
        <div class="permission-grid">
          <CInputCheckbox
            v-for="permission in group.items"
            :key="permission.code"
            class="permission-option"
            :class="{ 'permission-option--unavailable': !permission.available }"
            :label="permissionDisplayLabel(permission)"
            :value="permission.code"
            :disabled="!permission.available"
            :checked="roleForm.permissionCodes.includes(permission.code)"
            @update:checked="checked => togglePermission(roleForm.permissionCodes, permission, checked)"
          />
        </div>
      </div>
      <!--
        **押せない理由も、断られた理由も、押せないボタンの隣に出す。**

        このモーダルは機能の一覧で長くなる。作成を押す時点で、役割名の欄は
        画面の外にあることがほとんどで、**赤字を画面の上部（モーダルの外）へ
        入れても読めない。**諦めて閉じないと理由が分からない画面だった。
      -->
      <CAlert v-if="missingFields.length" color="warning" class="mb-3">
        <b>あと{{ missingFields.length }}つ入力すると、作成できます。</b>
        <ul class="mb-0 mt-2">
          <li v-for="what in missingFields" :key="what">{{ what }}</li>
        </ul>
      </CAlert>
      <CAlert v-if="roleFormError" color="danger" class="mb-3">
        {{ roleFormError }}
      </CAlert>
      <template #footer>
        <div class="modal-actions">
          <CButton color="secondary" @click="roleModal = false">キャンセル</CButton>
          <CButton
            color="primary"
            :disabled="saving || !roleForm.roleName"
            @click="createRole"
          >
            作成
          </CButton>
        </div>
      </template>
    </CModal>

    <CModal
      title="利用できる機能を編集"
      :show.sync="permissionModal"
      size="lg"
      :close-on-backdrop="false"
      class="access-modal"
    >
      <template v-if="editingRole">
        <p><strong>{{ editingRole.name }}</strong>で利用できる機能を選択してください。</p>
        <CAlert v-if="editingRole.standardRole && !editingRole.customized" color="info">
          保存すると、この会社専用の設定を作成します。他社の「{{ editingRole.name }}」には影響しません。
        </CAlert>
        <CAlert v-else color="secondary">
          この変更は現在の会社だけに適用されます。
        </CAlert>
        <CAlert color="info">
          現在の契約プランは<strong>{{ currentPlanName }}</strong>です。
          グレー表示の機能は新しく選択できません。
        </CAlert>
        <CAlert v-if="editingDormantPermissions.length" color="warning">
          この役割には、現在の契約では利用できない機能が{{ editingDormantPermissions.length }}件保存されています。
          プランを戻した場合に再利用できるよう設定を保持し、この画面では変更できません。
        </CAlert>
      </template>
      <div v-for="group in groupedPermissions" :key="group.name" class="permission-group">
        <h6>{{ group.name }}</h6>
        <div class="permission-grid">
          <CInputCheckbox
            v-for="permission in group.items"
            :key="permission.code"
            class="permission-option"
            :class="{ 'permission-option--unavailable': !permission.available }"
            :label="permissionDisplayLabel(permission)"
            :value="permission.code"
            :disabled="!permission.available"
            :checked="editingPermissionCodes.includes(permission.code)"
            @update:checked="checked => togglePermission(editingPermissionCodes, permission, checked)"
          />
        </div>
      </div>
      <!--
        **こちらのモーダルも、断られた理由は中へ出す。**

        追加のモーダルと同じ事故がここにも残っていた。更新が断られても
        このモーダルは閉じないので、赤字を画面の上部（モーダルの外）へ
        置くと暗幕の下に潜り、**押しても何も起きていないように見える。**

        しかも読もうとして閉じると、開き直したときに openPermissionEdit が
        サーバの内容で選び直すので、**やり直そうとするほど選んだ機能が
        消える。**理由が中にあれば、閉じずにそのまま直せる。
      -->
      <CAlert v-if="permissionFormError" color="danger" class="mb-3">
        {{ permissionFormError }}
      </CAlert>
      <template #footer>
        <div class="modal-actions">
          <CButton color="secondary" @click="permissionModal = false">キャンセル</CButton>
          <CButton color="primary" :disabled="saving" @click="savePermissions">更新</CButton>
        </div>
      </template>
    </CModal>
  </div>
</template>

<script>
import TenantOwnerManagement from '@/components/access/TenantOwnerManagement'
import api from '@/services/tenantAccessManagement'

const ROLE_LABELS = {
  TENANT_OWNER: '契約企業最高管理者',
  TENANT_SYSTEM_ADMIN: '社内システム管理者',
  EXECUTIVE_ADMIN: '経営管理者',
  CLERK: '事務担当者',
  EMPLOYEE: '一般社員'
}

const PERMISSION_LABELS = {
  ATTENDANCE_USE_SELF: ['勤怠・申請', '自分の勤怠を利用'],
  REQUEST_USE_SELF: ['勤怠・申請', '自分の申請を利用'],
  EXPENSE_USE_SELF: ['経費・精算', '自分の経費精算を利用'],
  APPROVAL_MANAGE: ['承認', '申請を承認・差戻し'],
  PROJECT_MANAGE: ['プロジェクト', 'プロジェクトを管理'],
  CLIENT_MANAGE: ['プロジェクト', '取引先を管理'],
  SALES_MANAGE: ['売上・請求', '売上情報を管理'],
  INVOICE_MANAGE: ['売上・請求', '請求書を管理'],
  MONTHLY_AGGREGATION_VIEW: ['集計・分析', '月次集計を閲覧'],
  MONTHLY_AGGREGATION_EXPORT: ['集計・分析', '月次集計を出力'],
  MANAGEMENT_INSIGHT_VIEW: ['集計・分析', '経営分析を閲覧'],
  USER_MANAGE: ['システム管理', '利用者を管理'],
  ROLE_MANAGE: ['システム管理', '役割と権限を管理'],
  ROLE_VIEW: ['システム管理', '役割の設定を確認'],
  ROLE_ASSIGN: ['システム管理', 'スタッフへ役割を設定'],
  PERMISSION_VIEW: ['システム管理', '利用機能の設定を確認'],
  SYSTEM_SETTING_MANAGE: ['システム管理', '自社設定を管理'],
  AUDIT_LOG_VIEW: ['システム管理', '操作履歴を閲覧'],
  CONTRACT_MANAGE: ['契約管理', '契約情報を管理']
}

export default {
  name: 'TenantAccessManagement',
  components: { TenantOwnerManagement },
  data () {
    return {
      loading: false,
      saving: false,
      errorMessage: '',
      /*
        役割の追加モーダル専用の赤字。**画面上部の errorMessage とは分ける。**
        モーダルが開いている間、上部の赤字は覆われていて読めない。
      */
      roleFormError: '',
      /* 利用機能を編集するモーダル専用の赤字。理由は roleFormError と同じ。 */
      permissionFormError: '',
      successMessage: '',
      users: [],
      roles: [],
      permissions: [],
      selectedRoleByUser: {},
      roleModal: false,
      permissionModal: false,
      editingRole: null,
      editingPermissionCodes: [],
      roleForm: { roleName: '', description: '', permissionCodes: [] },
      mobileUserPage: 1,
      mobileUsersPerPage: 5,
      userFields: [
        { key: 'name', label: '氏名' },
        { key: 'email', label: 'メールアドレス' },
        { key: 'roles', label: '現在の役割' },
        { key: 'actions', label: '役割を変更' }
      ],
      roleFields: [
        { key: 'name', label: '役割名' },
        { key: 'settingType', label: '設定' },
        { key: 'capabilities', label: 'できること' },
        { key: 'actions', label: '' }
      ]
    }
  },
  computed: {
    normalizedUsers () {
      return this.users.map(item => ({
        userId: this.value(item, 'userId', 'user_id'),
        name: this.value(item, 'name'),
        email: this.value(item, 'email'),
        roleCodes: this.arrayValue(item, 'roleCodes', 'role_codes')
      }))
    },
    mobileUserPageCount () {
      return Math.max(1, Math.ceil(this.normalizedUsers.length / this.mobileUsersPerPage))
    },
    mobileUsers () {
      const start = (this.mobileUserPage - 1) * this.mobileUsersPerPage
      return this.normalizedUsers.slice(start, start + this.mobileUsersPerPage)
    },
    normalizedPermissions () {
      return this.permissions.map(item => {
        const code = this.value(item, 'permissionCode', 'permission_code')
        const defined = PERMISSION_LABELS[code]
        const availableValue = this.value(item, 'featureEnabled', 'feature_enabled')
        return {
          code,
          group: defined ? defined[0] : 'その他',
          label: defined ? defined[1] : (this.value(item, 'permissionName', 'permission_name') || '名称未設定の機能'),
          featureCode: this.value(item, 'featureCode', 'feature_code'),
          featureName: this.value(item, 'featureName', 'feature_name'),
          available: availableValue === true || availableValue === 1 || String(availableValue).toLowerCase() === 'true',
          featureSource: this.value(item, 'featureSource', 'feature_source'),
          minimumPlanName: this.value(item, 'minimumPlanName', 'minimum_plan_name'),
          planName: this.value(item, 'planName', 'plan_name')
        }
      }).filter(permission => permission.code)
    },
    permissionByCode () {
      return this.normalizedPermissions.reduce((result, permission) => {
        result[permission.code] = permission
        return result
      }, {})
    },
    currentPlanName () {
      const withPlan = this.normalizedPermissions.find(permission => permission.planName)
      return withPlan ? withPlan.planName : '契約プラン未設定'
    },
    normalizedRoles () {
      return this.roles.map(item => {
        const code = this.value(item, 'roleCode', 'role_code')
        const permissionCodes = this.arrayValue(item, 'permissionCodes', 'permission_codes')
        const activePermissionCodes = permissionCodes.filter(permissionCode => this.isPermissionAvailable(permissionCode))
        const dormantPermissionCodes = permissionCodes.filter(permissionCode => !this.isPermissionAvailable(permissionCode))
        return {
          id: this.value(item, 'id'),
          code,
          name: ROLE_LABELS[code] || this.value(item, 'roleName', 'role_name') || '名称未設定の役割',
          scope: this.value(item, 'roleScope', 'role_scope'),
          systemRole: Boolean(this.value(item, 'systemRoleFlg', 'system_role_flg')),
          standardRole: Boolean(this.value(item, 'standardRoleFlg', 'standard_role_flg')),
          customized: Boolean(this.value(item, 'customizedFlg', 'customized_flg')),
          permissionCodes,
          activePermissionCodes,
          dormantPermissionCodes,
          activeCapabilityGroups: this.toCapabilityGroups(activePermissionCodes),
          dormantCapabilityGroups: this.toCapabilityGroups(dormantPermissionCodes)
        }
      }).filter(role => role.scope !== 'PLATFORM')
    },
    groupedPermissions () {
      const groups = []
      this.normalizedPermissions.forEach(permission => {
        let group = groups.find(item => item.name === permission.group)
        if (!group) {
          group = { name: permission.group, items: [] }
          groups.push(group)
        }
        group.items.push(permission)
      })
      return groups
    },
    editingDormantPermissions () {
      return this.editingPermissionCodes
        .map(code => this.permissionByCode[code])
        .filter(permission => permission && !permission.available)
    },
    /**
     * 役割の追加で、まだ入っていない必須項目の名前。
     *
     * **作成ボタンを無効にしている条件と、ここは同じものを見る。**片方だけ
     * 直すと「あと0つです」と出ているのに押せない、というさらに分かりにくい
     * 形になる。
     *
     * 画面に出ている言葉をそのまま使う。roleName と出しても、どこを直せば
     * よいか分からない。
     */
    missingFields () {
      const missing = []
      if (!this.roleForm.roleName) { missing.push('役割名') }
      return missing
    },
    roleOptions () {
      return [{ value: null, label: '役割を選択' }].concat(
        this.normalizedRoles.map(role => ({
          value: role.id,
          label: role.dormantPermissionCodes.length
            ? `${role.name}（一部は契約対象外）`
            : role.name
        }))
      )
    }
  },
  mounted () {
    this.loadAll()
  },
  methods: {
    value (item, ...keys) {
      for (const key of keys) {
        if (item && Object.prototype.hasOwnProperty.call(item, key)) return item[key]
        const upper = key.toUpperCase()
        if (item && Object.prototype.hasOwnProperty.call(item, upper)) return item[upper]
      }
      return null
    },
    arrayValue (item, ...keys) {
      const value = this.value(item, ...keys)
      if (Array.isArray(value)) return value
      if (typeof value === 'string') return value.replace(/[{}]/g, '').split(',').filter(Boolean)
      return []
    },
    isPermissionAvailable (code) {
      const permission = this.permissionByCode[code]
      return Boolean(permission && permission.available)
    },
    toCapabilityGroups (permissionCodes) {
      const groups = []
      permissionCodes.forEach(code => {
        const permission = this.permissionByCode[code]
        const defined = PERMISSION_LABELS[code]
        const groupName = permission ? permission.group : (defined ? defined[0] : 'その他')
        const label = permission ? permission.label : (defined ? defined[1] : code)
        let group = groups.find(item => item.name === groupName)
        if (!group) {
          group = { name: groupName, items: [] }
          groups.push(group)
        }
        group.items.push(label)
      })
      return groups
    },
    permissionDisplayLabel (permission) {
      if (permission.available) return permission.label
      if (permission.minimumPlanName) {
        return `${permission.label}（${permission.minimumPlanName}以上、または個別契約で利用可能）`
      }
      if (permission.featureName) {
        return `${permission.label}（現在の契約では「${permission.featureName}」を利用できません）`
      }
      return `${permission.label}（現在の契約では利用できません）`
    },
    settingLabel (role) {
      if (role.standardRole && role.customized) return '自社設定'
      if (role.standardRole) return '標準設定'
      return '独自役割'
    },
    settingBadgeColor (role) {
      if (role.standardRole && role.customized) return 'primary'
      if (role.standardRole) return 'secondary'
      return 'info'
    },
    async loadAll () {
      this.loading = true
      this.errorMessage = ''
      try {
        const [users, roles, permissions] = await Promise.all([
          api.getUsers(), api.getRoles(), api.getPermissions()
        ])
        this.permissions = Array.isArray(permissions) ? permissions : []
        this.roles = Array.isArray(roles) ? roles : []
        this.users = Array.isArray(users) ? users : []
        if (this.mobileUserPage > this.mobileUserPageCount) this.mobileUserPage = 1
      } catch (error) {
        this.handleError(error, '自社権限情報を取得できませんでした。')
      } finally {
        this.loading = false
      }
    },
    setMobileUserPage (page) {
      this.mobileUserPage = Math.min(Math.max(Number(page) || 1, 1), this.mobileUserPageCount)
      this.$nextTick(() => {
        const firstCard = this.$el.querySelector('.mobile-record-card')
        if (firstCard && typeof firstCard.scrollIntoView === 'function') {
          firstCard.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    roleLabel (code) {
      const role = this.normalizedRoles.find(item => item.code === code)
      return role ? role.name : (ROLE_LABELS[code] || '設定済みの役割')
    },
    setSelectedRole (userId, value) {
      this.$set(this.selectedRoleByUser, userId, value)
    },
    selectedRole (user) {
      return this.normalizedRoles.find(role => String(role.id) === String(this.selectedRoleByUser[user.userId]))
    },
    async assignRole (user) {
      const role = this.selectedRole(user)
      if (!role) return this.handleError(new Error('追加する役割を選択してください。'))
      if (user.roleCodes.includes(role.code)) return this.handleError(new Error('その役割は既に設定されています。'))
      await this.execute(
        () => api.assignRole({ userId: user.userId, roleId: role.id }),
        '役割を追加しました。'
      )
    },
    async removeRole (user) {
      const role = this.selectedRole(user)
      if (!role || !user.roleCodes.includes(role.code)) {
        return this.handleError(new Error('解除する現在の役割を選択してください。'))
      }
      await this.execute(
        () => api.removeRole({ userId: user.userId, roleId: role.id }),
        '役割を解除しました。'
      )
    },
    openCreateRole () {
      /*
        **ここで roleForm を空にしない。**

        以前はこの行で初期化していた。作成に失敗すると赤字はモーダルの外に
        出るので、利用者は読むためにいったん閉じる。そして開き直した瞬間、
        選んだ機能が全部消えていた。**やり直そうとするほど作業が消える。**

        空にするのは、役割を作り終えたときだけでよい。作りかけが残っていても
        名前と機能はそのまま画面に見えているので、迷いようがない。
      */
      this.roleFormError = ''
      this.roleModal = true
    },
    openPermissionEdit (role) {
      if (role.code === 'TENANT_OWNER') {
        return this.handleError(new Error('契約企業最高管理者の利用機能は変更できません。'))
      }
      this.editingRole = role
      this.editingPermissionCodes = role.permissionCodes.slice()
      this.permissionFormError = ''
      this.permissionModal = true
    },
    togglePermission (list, permission, checked) {
      if (!permission.available) {
        this.errorMessage = `${permission.label}は現在の契約では選択できません。`
        return
      }
      const index = list.indexOf(permission.code)
      if (checked && index < 0) list.push(permission.code)
      if (!checked && index >= 0) list.splice(index, 1)
    },
    async createRole () {
      this.roleFormError = ''
      if (!this.roleForm.roleName) {
        // ボタンを無効にしてあるので普通は通らない。念のための最後の壁。
        this.roleFormError = '役割名を入力してください。'
        return
      }
      const payload = Object.assign({}, this.roleForm, {
        permissionCodes: this.roleForm.permissionCodes.filter(code => this.isPermissionAvailable(code))
      })
      await this.execute(
        () => api.createRole(payload),
        '自社の役割を作成しました。',
        () => {
          this.roleModal = false
          // 作り終えたので、ここで初めて空にする。
          this.roleForm = { roleName: '', description: '', permissionCodes: [] }
        },
        // **断られた理由もモーダルの中へ。**外へ出しても覆われていて読めない。
        error => { this.roleFormError = this.errorText(error) }
      )
    },
    async savePermissions () {
      this.permissionFormError = ''
      await this.execute(
        () => api.replaceRolePermissions({
          roleId: this.editingRole.id,
          permissionCodes: this.editingPermissionCodes
        }),
        `${this.editingRole.name}で利用できる機能を更新しました。`,
        () => { this.permissionModal = false },
        // **断られた理由もモーダルの中へ。**外へ出しても覆われていて読めない。
        error => { this.permissionFormError = this.errorText(error) }
      )
    },
    /**
     * @param onError 赤字の置き場所を変えたいときに渡す。
     *   **モーダルの中で失敗したものは、中へ出さないと読めない。**
     */
    async execute (action, message, after, onError) {
      this.saving = true
      this.errorMessage = ''
      try {
        await action()
        if (after) after()
        this.successMessage = message
        await this.loadAll()
      } catch (error) {
        if (onError) {
          onError(error)
        } else {
          this.handleError(error)
        }
      } finally {
        this.saving = false
      }
    },
    /** 画面へ出してよい文言を選ぶ。出す場所は呼ぶ側が決める。 */
    errorText (error, fallback) {
      const responseMessage = error && error.response && error.response.data &&
        (error.response.data.message)
      // axiosが付ける英語のmessageは画面へ出さない。
      const ownMessage = error && !error.isAxiosError ? error.message : ''
      return responseMessage || ownMessage || fallback || '処理に失敗しました。'
    },
    handleError (error, fallback) {
      this.errorMessage = this.errorText(error, fallback)
    }
  }
}
</script>

<style scoped>
.tenant-access-page {
  min-width: 0;
  padding: 28px;
}
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
}
.page-header__copy {
  min-width: 0;
}
.page-header h1 {
  margin-bottom: 8px;
}
.page-header p {
  max-width: 760px;
  margin-bottom: 0;
  overflow-wrap: anywhere;
}
.page-header__action {
  flex: 0 0 auto;
  min-height: 42px;
}
.eyebrow {
  margin-bottom: 4px;
  color: #64748b;
  font-weight: 700;
  letter-spacing: .16em;
}
.plan-scope-alert {
  line-height: 1.7;
}
.access-card {
  min-width: 0;
  margin-bottom: 18px;
}
.responsive-table-shell {
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.responsive-table-shell::v-deep table {
  min-width: 860px;
}
.desktop-role-controls {
  min-width: 230px;
}
.desktop-role-buttons {
  display: flex;
  gap: 8px;
}
.role-badge-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
}
.role-badge-list .badge {
  white-space: normal;
  text-align: left;
  overflow-wrap: anywhere;
}
.capability-list {
  display: grid;
  gap: 8px;
  min-width: 0;
}
.capability-row {
  display: grid;
  grid-template-columns: minmax(110px, 160px) minmax(0, 1fr);
  gap: 12px;
  min-width: 0;
}
.capability-row strong {
  color: #334155;
}
.capability-row span {
  min-width: 0;
  overflow-wrap: anywhere;
}
.dormant-capability-block {
  display: grid;
  gap: 6px;
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px dashed #c7d2da;
  border-radius: 10px;
  background: #f4f6f8;
  color: #657681;
}
.dormant-capability-block > strong {
  font-size: 11px;
}
.dormant-capability-row {
  display: grid;
  grid-template-columns: minmax(110px, 150px) minmax(0, 1fr);
  gap: 10px;
  min-width: 0;
}
.dormant-capability-row span {
  font-size: 11px;
  font-weight: 800;
}
.dormant-capability-row small {
  min-width: 0;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.permission-group {
  padding: 12px 0;
  border-bottom: 1px solid #e2e8f0;
}
.permission-group:last-child {
  border-bottom: 0;
}
.permission-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 10px 16px;
  max-height: 420px;
  overflow-y: auto;
}
.permission-option {
  min-width: 0;
}
.permission-option--unavailable {
  padding: 8px 10px;
  border: 1px dashed #d6dde2;
  border-radius: 9px;
  background: #f4f6f8;
  opacity: .72;
}
.tenant-access-page::v-deep .permission-option--unavailable .custom-control-label {
  color: #677984;
  cursor: not-allowed;
}
/*
  表とカードは同じ中身で、画面幅に応じて片方だけを出す。

  切り替えは .mobile-only / .desktop-only が持っている。ところが同じ div に
  付けた一覧クラスにも display があり、後ろに書いた方が勝つため、広い画面
  でも表の下にカードが並んでいた。組み合わせで明示して、書き順に左右され
  ないようにする。
*/
.mobile-only {
  display: none;
}
.mobile-only.mobile-record-list {
  display: none;
}
.mobile-record-list {
  display: grid;
  gap: 14px;
}
.mobile-record-card {
  min-width: 0;
  padding: 16px;
  border: 1px solid #d8e4ec;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 8px 22px rgba(28, 68, 91, .07);
}
.mobile-record-card__header {
  display: grid;
  gap: 4px;
  min-width: 0;
  margin-bottom: 14px;
}
.mobile-record-card__header strong {
  color: #17384d;
  font-size: 17px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}
.mobile-record-card__header span {
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.mobile-section {
  min-width: 0;
  margin-bottom: 14px;
  padding: 13px;
  border-radius: 11px;
  background: #f5f9fc;
}
.mobile-section:last-child {
  margin-bottom: 0;
}
.mobile-section h3,
.mobile-section label {
  display: block;
  margin: 0 0 10px;
  color: #526b7b;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .03em;
}
.mobile-section--controls {
  background: #fff;
  border: 1px solid #e0e9ef;
}
.dormant-mobile-section {
  border: 1px dashed #c7d2da;
  background: #f4f6f8;
}
.capability-row--dormant {
  color: #657681;
}
.mobile-button-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.mobile-button-grid .btn,
.mobile-primary-action {
  width: 100%;
  min-height: 46px;
  margin: 0;
  white-space: normal;
}
.mobile-role-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
  margin-bottom: 14px;
}
.mobile-role-header strong {
  min-width: 0;
  color: #17384d;
  font-size: 17px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}
.mobile-role-header .badge {
  flex: 0 0 auto;
  white-space: nowrap;
}
.protected-role-note {
  padding: 11px 12px;
  border-radius: 10px;
  background: #f2f5f7;
  color: #667985;
  font-size: 12px;
  line-height: 1.6;
  text-align: center;
}
.mobile-empty-state {
  padding: 30px 14px;
  border: 1px dashed #cbd8e1;
  border-radius: 12px;
  color: #687d8a;
  text-align: center;
}
.mobile-pager {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding: 12px;
  border: 1px solid #dfe8ee;
  border-radius: 12px;
  background: #f8fbfd;
}
.mobile-pager .btn {
  min-height: 44px;
  margin: 0;
}
.mobile-pager span {
  min-width: 54px;
  color: #455f70;
  font-weight: 800;
  text-align: center;
  white-space: nowrap;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  width: 100%;
}

@media (max-width: 767.98px) {
  .tenant-access-page {
    padding: 12px 0 112px;
  }
  .page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
    margin-bottom: 16px;
  }
  .page-header h1 {
    font-size: 28px;
    line-height: 1.25;
  }
  .page-header__action {
    width: 100%;
    min-height: 46px;
    margin: 0;
    white-space: normal;
  }
  .desktop-only {
    display: none !important;
  }
  .mobile-only {
    display: block;
  }
  .mobile-only.mobile-record-list {
    display: grid;
  }
  .tenant-access-page::v-deep .card-header {
    padding: 14px;
  }
  .tenant-access-page::v-deep .card-body {
    padding: 12px;
  }
  .mobile-role-header {
    flex-wrap: wrap;
  }
  .capability-row,
  .dormant-capability-row {
    grid-template-columns: minmax(0, 1fr);
    gap: 4px;
    padding: 8px 0;
    border-bottom: 1px solid #dce7ee;
  }
  .capability-row:last-child,
  .dormant-capability-row:last-child {
    border-bottom: 0;
  }
  .permission-grid {
    grid-template-columns: minmax(0, 1fr);
    max-height: none;
    overflow: visible;
  }
  .tenant-access-page::v-deep .permission-option .custom-control-label {
    white-space: normal;
    line-height: 1.55;
    overflow-wrap: anywhere;
  }
  .modal-actions {
    flex-direction: column-reverse;
    gap: 8px;
  }
  .modal-actions .btn {
    width: 100%;
    min-height: 46px;
    margin: 0;
    white-space: normal;
  }
  .tenant-access-page::v-deep .access-modal .modal-dialog {
    width: auto;
    max-width: calc(100vw - 16px);
    margin: 8px;
  }
  .tenant-access-page::v-deep .access-modal .modal-body {
    max-height: calc(100vh - 190px);
    overflow-y: auto;
  }
  .tenant-access-page::v-deep .access-modal .modal-footer {
    padding: 12px;
  }
}

@media (min-width: 768px) {
  .desktop-only {
    display: block;
  }
}
</style>
